/**
 * Feature Flags Configuration
 *
 * Controls gradual rollout of new features.
 * Flags can be toggled via localStorage for testing.
 */

/**
 * Read feature flag from localStorage with fallback to default.
 * @param {string} key - localStorage key
 * @param {boolean} defaultValue - default value if not set
 * @returns {boolean}
 */
function getFlag(key, defaultValue) {
  const stored = localStorage.getItem(key);
  if (stored === null) return defaultValue;
  return stored === 'true';
}

/**
 * Feature flags object.
 * Use localStorage to override in development:
 *   localStorage.setItem('FF_NEW_NAV', 'true')
 */
export const FEATURE_FLAGS = {
  /**
   * Use new TypeScript navigation module (Sidebar, Layout, ProjectSelector).
   * When true: uses @navigation/components
   * When false: uses legacy frontend/src/components/Layout/*
   */
  USE_NEW_NAVIGATION: getFlag('FF_NEW_NAV', true), // DEFAULT: true for testing
};

/**
 * Toggle feature flag in localStorage and reload.
 * For development use only.
 * @param {string} flagKey - key in FEATURE_FLAGS
 */
export function toggleFeatureFlag(flagKey) {
  const storageKey = `FF_${flagKey.replace('USE_', '')}`;
  const current = localStorage.getItem(storageKey) === 'true';
  localStorage.setItem(storageKey, (!current).toString());
  window.location.reload();
}

/**
 * Get current status of navigation flag
 * @returns {boolean}
 */
export function isNewNavigationEnabled() {
  return FEATURE_FLAGS.USE_NEW_NAVIGATION;
}

// Development-only: keyboard shortcut and console helpers
if (process.env.NODE_ENV === 'development') {
  // Ctrl+Shift+N toggles navigation
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey && e.shiftKey && e.key === 'N') {
      e.preventDefault();
      toggleFeatureFlag('USE_NEW_NAVIGATION');
    }
  });

  // Console helper to check current state
  window.__NAV_STATUS__ = () => {
    const isNew = FEATURE_FLAGS.USE_NEW_NAVIGATION;
    console.log(`%c Navigation: ${isNew ? 'NEW (TypeScript)' : 'LEGACY (JavaScript)'}`,
      `color: ${isNew ? 'green' : 'orange'}; font-weight: bold; font-size: 14px;`);
    console.log('Toggle with: Ctrl+Shift+N or window.__NAV_TOGGLE__()');
    return isNew;
  };

  window.__NAV_TOGGLE__ = () => toggleFeatureFlag('USE_NEW_NAVIGATION');

  // Auto-log on load
  setTimeout(() => {
    window.__NAV_STATUS__();
  }, 1000);
}
